/* =========================================
   Quick Order Form — JavaScript
   ========================================= */

(function () {
  'use strict';

  const state = {
    products: [],
    quantities: {},
    searchQuery: '',
    adding: false,
    loaded: false,
    bagItems: 0,
    bagCents: 0,
    hideTypes: [],        // product_type values to exclude (lowercase)
    hideTags: [],         // tag values to exclude (lowercase)
    hideSkuSuffixes: [],  // variant SKU suffixes to exclude (uppercase)
  };

  /* ---- Helpers ---- */
  function show(el) { if (el) el.style.display = ''; }
  function hide(el) { if (el) el.style.display = 'none'; }
  function showFlex(el) { if (el) el.style.display = 'flex'; }

  function money(cents) {
    if (typeof formatMoney === 'function' && window.FoxThemeSettings && window.FoxThemeSettings.money_format) {
      return formatMoney(cents, window.FoxThemeSettings.money_format);
    }
    return '£' + (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ---- Fetch all products, page by page ---- */
  async function fetchAllProducts() {
    const all = [];
    let page = 1;
    while (true) {
      let data;
      try {
        const resp = await fetch('/products.json?limit=250&page=' + page);
        if (!resp.ok) break;
        data = await resp.json();
      } catch (e) {
        break; // stop on any parse error, keep what we have
      }
      if (!data.products || data.products.length === 0) break;
      all.push(...data.products);
      if (data.products.length < 250) break;
      page++;
    }
    return all;
  }

  /* ---- Flatten products into variant rows ---- */
  function flatten(products) {
    const rows = [];
    products.forEach(function (product) {
      // Apply product-type exclusions
      if (state.hideTypes.length) {
        const pType = (product.product_type || '').toLowerCase().trim();
        if (state.hideTypes.includes(pType)) return;
      }

      // Apply tag exclusions — /products.json returns tags as a comma-separated string
      if (state.hideTags.length) {
        const pTags = (product.tags || '').toLowerCase().split(',').map(function (t) { return t.trim(); });
        if (state.hideTags.some(function (t) { return pTags.includes(t); })) return;
      }

      const defaultOnly =
        product.variants.length === 1 &&
        product.variants[0].title === 'Default Title';

      product.variants.forEach(function (variant) {
        const rawSrc =
          (variant.featured_image && variant.featured_image.src) ||
          (product.images.length > 0 && product.images[0].src) ||
          null;

        const thumbSrc = rawSrc
          ? rawSrc.replace(/(\.[a-zA-Z]+)(\?.*)?$/, '_100x100$1$2')
          : null;

        const sku = (variant.sku || '').trim();

        // Skip variants whose SKU ends with an excluded suffix (e.g. P, E)
        if (state.hideSkuSuffixes.length && sku) {
          const skuUpper = sku.toUpperCase();
          const blocked = state.hideSkuSuffixes.some(function (suffix) {
            return skuUpper.endsWith(suffix.toUpperCase());
          });
          if (blocked) {
            console.log('[QOF] Blocking SKU:', sku, '(suffix match)');
            return;
          }
        }

        rows.push({
          variantId: String(variant.id),
          productHandle: product.handle,
          displayTitle: defaultOnly
            ? product.title
            : product.title + ' — ' + variant.title,
          sku: sku,
          price: Math.round(parseFloat(variant.price) * 100),
          image: thumbSrc,
          available: variant.available,
        });
      });
    });
    return rows;
  }

  function filtered() {
    const q = state.searchQuery.toLowerCase().trim();
    if (!q) return state.products;
    return state.products.filter(function (r) {
      return r.displayTitle.toLowerCase().indexOf(q) !== -1 ||
             r.sku.toLowerCase().indexOf(q) !== -1;
    });
  }

  /* ---- Totals ---- */
  function updateTotals() {
    // Tally the current (unsubmitted) form selection
    let selectionItems = 0;
    let selectionCents = 0;
    Object.keys(state.quantities).forEach(function (id) {
      const qty = state.quantities[id] || 0;
      if (qty > 0) {
        selectionItems += qty;
        const row = state.products.find(function (r) { return r.variantId === id; });
        if (row) selectionCents += row.price * qty;
      }
    });

    // Footer always shows the confirmed bag total PLUS whatever is currently selected
    const displayItems = state.bagItems + selectionItems;
    const displayCents = state.bagCents + selectionCents;

    const countEl = document.getElementById('qof-item-count');
    const subtotalEl = document.getElementById('qof-subtotal');
    const addBtn = document.getElementById('qof-add-btn');
    const clearBtn = document.getElementById('qof-clear-btn');

    if (countEl) countEl.textContent = displayItems === 1 ? '1 item' : displayItems + ' items';
    if (subtotalEl) subtotalEl.textContent = money(displayCents);
    // Add to Bag only enables when there's something in the current selection
    if (addBtn) addBtn.disabled = selectionItems === 0 || state.adding;
    if (clearBtn) clearBtn.style.display = selectionItems > 0 ? 'inline-flex' : 'none';
  }

  /* ---- Build a single table row ---- */
  function buildRow(row) {
    const qty = state.quantities[row.variantId] || 0;
    const tr = document.createElement('tr');
    tr.className = 'quick-order-form__row' + (row.available ? '' : ' qof-row--unavailable');
    tr.dataset.variantId = row.variantId;

    const imgHtml = row.image
      ? '<img class="quick-order-form__product-img" src="' + row.image + '" alt="" width="50" height="50" loading="lazy">'
      : '<div class="quick-order-form__product-img-placeholder"></div>';

    const priceHtml =
      '<div class="quick-order-form__price-cell">' +
      '<span class="quick-order-form__price">' + money(row.price) + '</span>' +
      (row.available
        ? '<span class="quick-order-form__badge quick-order-form__badge--instock">In stock</span>'
        : '<span class="quick-order-form__badge quick-order-form__badge--oos">Out of stock</span>'
      ) +
      '</div>';

    const qtyHtml = row.available
      ? '<div class="quick-order-form__qty-wrap">' +
          '<div class="quick-order-form__qty-control">' +
            '<button class="quick-order-form__qty-btn qof-qty-minus" type="button" aria-label="Decrease"' + (qty <= 0 ? ' disabled' : '') + '>' +
              '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M5 12h14"/></svg>' +
            '</button>' +
            '<input class="quick-order-form__qty-input" type="number" min="0" step="1" value="' + qty + '" data-variant-id="' + row.variantId + '" aria-label="Quantity">' +
            '<button class="quick-order-form__qty-btn qof-qty-plus" type="button" aria-label="Increase">' +
              '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>' +
            '</button>' +
          '</div>' +
          '<button class="quick-order-form__remove-btn qof-remove-btn" type="button" aria-label="Remove from selection"' + (qty <= 0 ? ' style="visibility:hidden"' : '') + '>' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>' +
          '</button>' +
        '</div>'
      : '<span class="quick-order-form__oos-text">—</span>';

    tr.innerHTML =
      '<td class="quick-order-form__td qof-col-product">' +
        '<div class="quick-order-form__product-cell">' + imgHtml +
          '<div class="quick-order-form__product-info">' +
            '<a href="/products/' + row.productHandle + '" class="quick-order-form__product-name">' + escapeHtml(row.displayTitle) + '</a>' +
          '</div>' +
        '</div>' +
      '</td>' +
      '<td class="quick-order-form__td qof-col-sku"><span class="quick-order-form__sku">' + escapeHtml(row.sku || '—') + '</span></td>' +
      '<td class="quick-order-form__td qof-col-price">' + priceHtml + '</td>' +
      '<td class="quick-order-form__td qof-col-qty">' + qtyHtml + '</td>';

    return tr;
  }

  /* ---- Render / re-render the table ---- */
  function renderTable() {
    const tbody = document.getElementById('qof-tbody');
    const tableEl = document.getElementById('qof-table');
    const emptyEl = document.getElementById('qof-empty');
    if (!tbody) return;

    const rows = filtered();
    tbody.innerHTML = '';

    if (rows.length === 0) {
      hide(tableEl);
      showFlex(emptyEl);
    } else {
      show(tableEl);
      hide(emptyEl);
      rows.forEach(function (row) { tbody.appendChild(buildRow(row)); });
    }
  }

  /* ---- Set quantity for a variant ---- */
  function setQty(variantId, rawValue) {
    const qty = Math.max(0, parseInt(rawValue, 10) || 0);
    state.quantities[variantId] = qty;

    const input = document.querySelector('.quick-order-form__qty-input[data-variant-id="' + variantId + '"]');
    if (input) {
      input.value = qty;
      const control = input.closest('.quick-order-form__qty-control');
      if (control) {
        const minus = control.querySelector('.qof-qty-minus');
        if (minus) minus.disabled = qty <= 0;
      }
      const removeBtn = input.closest('.quick-order-form__qty-wrap') &&
                        input.closest('.quick-order-form__qty-wrap').querySelector('.qof-remove-btn');
      if (removeBtn) removeBtn.style.visibility = qty <= 0 ? 'hidden' : 'visible';
    }

    updateTotals();
  }

  /* ---- Table event delegation ---- */
  function onTbodyClick(e) {
    const minus = e.target.closest('.qof-qty-minus');
    const plus = e.target.closest('.qof-qty-plus');
    const remove = e.target.closest('.qof-remove-btn');

    if (remove) {
      const wrap = remove.closest('.quick-order-form__qty-wrap');
      const input = wrap && wrap.querySelector('.quick-order-form__qty-input');
      if (input) setQty(input.dataset.variantId, 0);
      return;
    }

    if (!minus && !plus) return;
    const control = (minus || plus).closest('.quick-order-form__qty-control');
    if (!control) return;
    const input = control.querySelector('.quick-order-form__qty-input');
    if (!input) return;
    setQty(input.dataset.variantId, (parseInt(input.value, 10) || 0) + (minus ? -1 : 1));
  }

  function onTbodyChange(e) {
    const input = e.target;
    if (!input.classList.contains('quick-order-form__qty-input')) return;
    setQty(input.dataset.variantId, input.value);
  }

  /* ---- Sync bag total from the real Shopify cart ---- */
  async function syncBagTotal() {
    try {
      const resp = await fetch('/cart.js');
      const cart = await resp.json();
      // total_price from /cart.js is already in cents (integer)
      state.bagItems = cart.item_count || 0;
      state.bagCents = cart.total_price || 0;
      updateTotals();

      // Show persistent buttons if there's anything in the bag
      if (cart.item_count > 0) {
        const viewBagBtn = document.getElementById('qof-view-bag-btn');
        if (viewBagBtn) viewBagBtn.style.display = 'inline-flex';
        const checkoutBtn = document.getElementById('qof-checkout-btn');
        if (checkoutBtn) checkoutBtn.style.display = 'inline-flex';
      }

      // Keep the theme header cart count in sync
      if (window.FoxThemeCartHelpers && typeof window.FoxThemeCartHelpers.updateCartCount === 'function') {
        window.FoxThemeCartHelpers.updateCartCount(cart.item_count);
      }
    } catch (e) { /* non-fatal — footer just stays as-is */ }
  }

  /* ---- Add to cart ---- */
  async function addToCart() {
    if (state.adding) return;

    const items = [];
    Object.keys(state.quantities).forEach(function (id) {
      const qty = state.quantities[id] || 0;
      if (qty > 0) items.push({ id: parseInt(id, 10), quantity: qty });
    });
    if (!items.length) return;

    state.adding = true;
    const addBtn = document.getElementById('qof-add-btn');
    const btnText = addBtn && addBtn.querySelector('.qof-btn-text');
    if (btnText) btnText.textContent = 'Adding to bag…';
    if (addBtn) addBtn.disabled = true;

    try {
      const resp = await fetch('/cart/add.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: items }),
      });

      // Guard against HTML error pages
      const contentType = resp.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Unexpected response from cart. Please try again.');
      }

      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.description || data.message || 'Could not add items to cart.');
      }

      // Clear form selection, then sync footer from the real cart
      state.quantities = {};
      renderTable();
      showAlert('Items added to your bag! You can continue adding or proceed to checkout.', 'success');

      // Tell the theme's cart drawer to re-render its contents
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));

      // syncBagTotal fetches /cart.js, updates state.bagItems/bagCents, calls updateTotals,
      // and shows the View Shopping Bag / Checkout buttons
      await syncBagTotal();

    } catch (err) {
      showAlert(err.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      state.adding = false;
      if (btnText) btnText.textContent = 'Add to Bag';
      updateTotals();
    }
  }

  /* ---- Alert ---- */
  function showAlert(msg, type) {
    const el = document.getElementById('qof-alert');
    if (!el) return;
    el.textContent = msg;
    el.className = 'quick-order-form__alert quick-order-form__alert--' + type;
    el.style.display = 'block';
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.style.display = 'none'; }, 5000);
  }

  /* ---- Init ---- */
  async function init() {
    const section = document.querySelector('[data-section-type="quick-order-form"]');
    if (!section) return;

    // Parse exclusion lists from section data attributes (set via theme settings)
    function parseList(str) {
      return (str || '').toLowerCase().split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    }
    state.hideTypes      = parseList(section.dataset.hideTypes);
    state.hideTags       = parseList(section.dataset.hideTags);
    state.hideSkuSuffixes = parseList(section.dataset.hideSkuSuffixes);

    // Diagnostic — check browser Console (F12) to see what values were read
    console.log('[QOF] data-hide-sku-suffixes attr:', JSON.stringify(section.dataset.hideSkuSuffixes));
    console.log('[QOF] state.hideSkuSuffixes:', JSON.stringify(state.hideSkuSuffixes));

    const loadingEl = document.getElementById('qof-loading');
    const tableEl = document.getElementById('qof-table');
    const emptyEl = document.getElementById('qof-empty');

    // Start with only loading visible
    hide(tableEl);
    hide(emptyEl);
    showFlex(loadingEl);

    const rawProducts = await fetchAllProducts();
    state.products = flatten(rawProducts);
    state.loaded = true;

    hide(loadingEl);
    renderTable();

    // Initialise footer from the real cart (handles page refresh & pre-existing bag items)
    syncBagTotal();

    const searchInput = document.getElementById('qof-search');
    if (searchInput) {
      // Apply any pre-filled value from the theme setting
      if (searchInput.value.trim()) {
        state.searchQuery = searchInput.value.trim();
        renderTable();
      }
      searchInput.addEventListener('input', function () {
        if (!state.loaded) return;
        state.searchQuery = this.value;
        renderTable();
      });
    }

    const tbody = document.getElementById('qof-tbody');
    if (tbody) {
      tbody.addEventListener('click', onTbodyClick);
      tbody.addEventListener('change', onTbodyChange);
    }

    const addBtn = document.getElementById('qof-add-btn');
    if (addBtn) addBtn.addEventListener('click', addToCart);

    // Clear all selections
    const clearBtn = document.getElementById('qof-clear-btn');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        state.quantities = {};
        renderTable();
        updateTotals();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
